import { useState, useEffect, useCallback, useRef } from 'react';
import { horodatyApi } from '@/lib/utils/horodaty-api';

/**
 * Hook pour récupérer un template de procédure par son uniq_id
 *
 * @param {string | undefined} uniqId - Le uniq_id du template
 * @param {Object} [options]
 * @param {string[]} [options.toLoad] - Relations à charger ('allowedUsers', 'subfolders')
 *
 * @returns {{
 *   template: {
 *     uniq_id: string,
 *     name: string,
 *     description?: string,
 *     color: string,
 *     configuration: { sections: Array<...> },
 *     share_subsidiaries: boolean,
 *     creator: { company?: string, user?: string },
 *     allowed_users?: Array<...>,
 *     subfolders?: Array<...>,
 *     created_at: string,
 *     updated_at: string
 *   } | null,
 *   isLoading: boolean,
 *   error: Error | null,
 *   notFound: boolean,
 *   refetch: () => Promise<void>
 * }}
 */
export function useProcedureTemplate(uniqId, options = {}) {
    const { toLoad = [] } = options;

    const [template, setTemplate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const isMountedRef = useRef(true);

    const fetchTemplate = useCallback(async () => {
        if (!uniqId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        setNotFound(false);

        try {
            const params = toLoad.length > 0 ? { to_load: toLoad } : undefined;
            const response = await horodatyApi.procedureTemplates.get(uniqId, params);

            if (isMountedRef.current) {
                // Format: { success: true, data: { template: {...} } }
                const templateData = response.data?.data?.template || response.data?.template || response.data?.data;
                setTemplate(templateData);
                setIsLoading(false);
            }
        } catch (err) {
            if (isMountedRef.current) {
                if (err.response?.status === 404) {
                    setNotFound(true);
                } else {
                    setError(err);
                }
                setIsLoading(false);
            }
        }
    }, [uniqId, toLoad.join(',')]);

    useEffect(() => {
        isMountedRef.current = true;
        fetchTemplate();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchTemplate]);

    return {
        template,
        isLoading,
        error,
        notFound,
        refetch: fetchTemplate,
    };
}
